import { useMemo, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSiteData } from "@/context/SiteDataContext";
import { api, formatApiError, todayISO, waLink } from "@/lib/api";
import { getAttribution, markLeadSent } from "@/lib/attribution";
import { BUDGETS, DURATIONS, LEAD_DESTINATIONS, NON_PACKAGE_TRIPS, TRIP_TYPES } from "@/lib/constants";
import { WhatsAppIcon } from "./WhatsAppIcon";

const Field = ({ id, label, className = "", children }) => (
  <div className={`space-y-1.5 ${className}`}><Label htmlFor={id}>{label}</Label>{children}</div>
);

const Choice = ({ id, label, value, onChange, options, placeholder, testId }) => (
  <Field id={id} label={label} className="col-span-2 sm:col-span-1">
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="h-11" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

const Success = ({ done, settings, tid, onReset }) => {
  const pax = `${done.adults} adult${done.adults === 1 ? "" : "s"}${done.children ? `, ${done.children} child${done.children === 1 ? "" : "ren"}` : ""}`;
  const msg = `Hi Ocean Wings Travel, I just sent an enquiry. Name: ${done.name}, Travel date: ${done.travel_date}, Guests: ${pax}${done.trip_type ? `, Requirement: ${done.trip_type}` : ""}${done.package_name ? `, Package: ${done.package_name}` : ""}.`;
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 sm:p-8 text-center" data-testid={tid("success")}>
      <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
      <h3 className="mt-4 font-display text-3xl text-navy">Thank you, {done.name.split(" ")[0]}!</h3>
      <p className="mt-2 text-sm text-slate-600">Your enquiry is with our Andaman team. Want a faster reply? Continue on WhatsApp.</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <a href={waLink(settings.whatsapp, msg)} target="_blank" rel="noreferrer" data-testid={tid("success-whatsapp")}
          className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors">
          <WhatsAppIcon className="h-4 w-4" /> Continue on WhatsApp
        </a>
        <Button variant="outline" className="rounded-full h-11" data-testid={tid("new")} onClick={onReset}>Send another enquiry</Button>
      </div>
    </div>
  );
};

const PackageSelect = ({ value, onChange, packages, tid }) => {
  const grouped = useMemo(() => DURATIONS.map((d) => [d, packages.filter((p) => p.duration === d)]), [packages]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={tid("package")} className="h-11" data-testid={tid("package-select")}><SelectValue placeholder="Select a package" /></SelectTrigger>
      <SelectContent className="max-h-72 bg-white">
        <SelectItem value="none">Not sure yet – please suggest</SelectItem>
        {grouped.map(([d, list]) => list.length > 0 && (
          <SelectGroup key={d}>
            <SelectLabel className="text-ocean">{d}</SelectLabel>
            {list.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
};

export const EnquiryForm = ({ defaultPackageId = "", defaultTripType = "", defaultMessage = "", idPrefix = "enquiry", compact = false, submitLabel = "Get Free Quote" }) => {
  const { packages, settings } = useSiteData();
  const init = () => ({ name: "", phone: "", whatsapp: "", email: "", travel_date: "", adults: 2, children: 0, message: defaultMessage, hp: "", destination: "", budget: "",
    trip_type: defaultTripType || packages.find((p) => p.id === defaultPackageId)?.category || "", package_id: defaultPackageId || "none" });
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tid = (s) => `${idPrefix}-${s}`;
  const showPackage = !compact && !NON_PACKAGE_TRIPS.includes(form.trip_type);
  const pickPackage = (v) => setForm((f) => ({ ...f, package_id: v, trip_type: f.trip_type || packages.find((p) => p.id === v)?.category || "" }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, ...getAttribution(), form: idPrefix.slice(0, 40), adults: Number(form.adults), children: Number(form.children) || 0,
        email: form.email || null, package_id: showPackage && form.package_id !== "none" ? form.package_id : null };
      const { data } = await api.post("/enquiries", payload);
      markLeadSent();
      setDone(data);
      toast.success("Enquiry received! Our team will contact you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success done={done} settings={settings} tid={tid} onReset={() => { setDone(null); setForm(init()); }} />;

  return (
    <form onSubmit={submit} className="grid gap-4 grid-cols-2" data-testid={tid("form")}>
      <Field id={tid("name")} label="Full name *" className="col-span-2 sm:col-span-1">
        <Input id={tid("name")} required minLength={2} maxLength={100} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Your name" data-testid={tid("name-input")} className="h-11" />
      </Field>
      <Field id={tid("phone")} label="Mobile number *" className="col-span-2 sm:col-span-1">
        <Input id={tid("phone")} required type="tel" inputMode="tel" autoComplete="tel" pattern="^\+?[0-9][0-9\s\-]{6,19}$" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" data-testid={tid("phone-input")} className="h-11" />
      </Field>
      <Field id={tid("date")} label="Travel date *" className="col-span-2 sm:col-span-1">
        <Input id={tid("date")} required type="date" min={todayISO()} value={form.travel_date} onChange={set("travel_date")} data-testid={tid("date-input")} className="h-11" />
      </Field>
      <div className="col-span-2 sm:col-span-1 grid grid-cols-2 gap-3">
        <Field id={tid("adults")} label="Adults *">
          <Input id={tid("adults")} required type="number" inputMode="numeric" min={1} max={60} value={form.adults} onChange={set("adults")} data-testid={tid("adults-input")} className="h-11" />
        </Field>
        <Field id={tid("children")} label="Children">
          <Input id={tid("children")} type="number" inputMode="numeric" min={0} max={40} value={form.children} onChange={set("children")} data-testid={tid("children-input")} className="h-11" />
        </Field>
      </div>
      <Field id={tid("trip")} label="Requirement" className="col-span-2">
        <Select value={form.trip_type} onValueChange={(v) => setForm((f) => ({ ...f, trip_type: v }))}>
          <SelectTrigger id={tid("trip")} className="h-11" data-testid={tid("trip-type-select")}><SelectValue placeholder="Package, hotel, ferry..." /></SelectTrigger>
          <SelectContent className="bg-white">{TRIP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {showPackage && (
        <Field id={tid("package")} label="Package" className="col-span-2">
          <PackageSelect value={form.package_id} onChange={pickPackage} packages={packages} tid={tid} />
        </Field>
      )}
      {!compact && (
        <>
          <Choice id={tid("destination")} label="Destination" value={form.destination} onChange={(v) => setForm((f) => ({ ...f, destination: v }))}
            options={LEAD_DESTINATIONS} placeholder="Islands you want to visit" testId={tid("destination-select")} />
          <Choice id={tid("budget")} label="Budget" value={form.budget} onChange={(v) => setForm((f) => ({ ...f, budget: v }))}
            options={BUDGETS} placeholder="Per person budget" testId={tid("budget-select")} />
          <Field id={tid("whatsapp")} label="WhatsApp (if different)" className="col-span-2 sm:col-span-1">
            <Input id={tid("whatsapp")} type="tel" inputMode="tel" pattern="^\+?[0-9][0-9\s\-]{6,19}$" value={form.whatsapp} onChange={set("whatsapp")} placeholder="Same as mobile" data-testid={tid("whatsapp-input")} className="h-11" />
          </Field>
          <Field id={tid("email")} label="Email (for itinerary)" className="col-span-2 sm:col-span-1">
            <Input id={tid("email")} type="email" autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid={tid("email-input")} className="h-11" />
          </Field>
          <Field id={tid("message")} label="Message (optional)" className="col-span-2">
            <Textarea id={tid("message")} rows={3} maxLength={1000} value={form.message} onChange={set("message")} placeholder="Hotel preferences, islands, activities, special occasions..." data-testid={tid("message-input")} />
          </Field>
        </>
      )}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.hp} onChange={set("hp")} className="hidden" aria-hidden="true" />
      <Button type="submit" disabled={loading} data-testid={tid("submit-button")} className="col-span-2 h-12 rounded-full bg-ocean hover:bg-navy text-base font-semibold">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
      </Button>
      <p className="col-span-2 -mt-1 text-center text-xs text-slate-500">Free, no-obligation quote. We never share your number.</p>
    </form>
  );
};
